// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// =============================================================================
// ImportPage — bulk-import shelters from a CSV
// =============================================================================
// Renders: a 3-step wizard: upload CSV → preview parsed rows + conflict
//          resolution → execute. Inserts into `shelters` and (when provided)
//          inserts a default `delivery_addresses` row per shelter, content-deduped
//          against that shelter's existing addresses.
// Notes: each row's success/failure is logged into an in-page report so the
//        admin can re-run with fixes.
// Session:
//   user - current admin user (for audit + permission gating), read from useSession
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { insertAddresses, listAllByShelterId } from "../../data/deliveryAddresses";
import { upsertBankDetails } from "../../data/shelterBankDetails";
import {
  getShelterByCode,
  insertShelter,
  listSheltersByCodes,
  updateShelterByCode,
} from "../../data/shelters";
import {
  padAuPostcode,
  parseCsvCells,
  planDefaultDeliveryAddress,
} from "../../services/deliveryImport";
import { Btn, Card } from "../../utils/ui.jsx";
import { ContactsImport } from "./ContactsImport.jsx";
import {
  ContactLinkSection,
  CustomerLinkSection,
  CustomerSyncSection,
} from "./CustomerSyncUtilities.jsx";
import { DeliveryAddressImport } from "./DeliveryAddressImport.jsx";
import { UnleashedInspector } from "./UnleashedInspector.jsx";

// The Unleashed Customer export has NO customer-level State column, so derive it
// from the Customer Code — e.g. "ZephyrQLD001" → "QLD" (strip the Zephyr prefix +
// trailing number, uppercase the state letters). Case-insensitive on the prefix.
const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
function deriveStateFromCode(code) {
  const m = /^zephyr([a-z]{2,3})\d+/i.exec(String(code || "").trim());
  const st = m ? m[1].toUpperCase() : "";
  return AU_STATES.includes(st) ? st : "";
}

export function ImportPage() {
  // `user` now comes from SessionContext rather than a prop (Phase 3 refactor).
  const { user } = useSession();
  const [step, setStep] = React.useState("upload"); // upload | preview | importing | done
  const [rows, setRows] = React.useState([]);
  const [conflicts, setConflicts] = React.useState([]); // rows where shelter_code already exists
  const [conflictDecisions, setConflictDecisions] = React.useState({}); // id -> "update"|"skip"
  const [_importing, setImporting] = React.useState(false);
  const [importLog, setImportLog] = React.useState([]);
  const [fileErr, setFileErr] = React.useState("");

  // Parse the Unleashed Customer export. It's a COMMA CSV (RFC-4180: quoted
  // fields with embedded commas/newlines/quotes, BOM) — NOT tab-separated — so we
  // use the shared parser (same as the Delivery-Address + Schools imports). The
  // leading "*" on "*Customer Code" is stripped; header-driven (column order is
  // irrelevant).
  function parseCSV(text) {
    const cells = parseCsvCells(text);
    if (cells.length < 2) return [];
    const headers = cells[0].map(function (h) {
      return h.replace(/^\*/, "").trim();
    });
    function idx(name) {
      return headers.indexOf(name);
    }
    function get(cols, name) {
      const j = idx(name);
      return j === -1 ? "" : (cols[j] || "").trim();
    }
    const results = [];
    for (let i = 1; i < cells.length; i++) {
      const cols = cells[i];
      const mapped = {
        shelter_code: get(cols, "Customer Code"),
        shelter_name: get(cols, "Customer Name"),
        customer_type: get(cols, "Customer Type"),
        taxable: get(cols, "Taxable").toUpperCase() !== "FALSE",
        // db-127: Stop Credit (bool) + bank details from the Unleashed Customer CSV.
        stop_credit: ["TRUE", "YES", "Y", "1"].includes(get(cols, "Stop Credit").toUpperCase()),
        bank_name: get(cols, "Bank Name"),
        bank_account: get(cols, "Bank Account"),
        sales_group: get(cols, "Sales Group Name"),
        // Store the Unleashed warehouse CODE (db-58); the dropdown shows the name.
        default_warehouse: get(cols, "Default Warehouse Code"),
        postal_line1: get(cols, "Postal Street Address/PO Box"),
        postal_line2: get(cols, "Postal Street Address 2"),
        postal_suburb: get(cols, "Postal Suburb"),
        postal_suburb_city: get(cols, "Postal Town/City"),
        postal_state: get(cols, "Postal State/Region"),
        postal_postcode: padAuPostcode(get(cols, "Postal Postal Code")),
        delivery_instructions:
          get(cols, "Postal Delivery Instruction") || get(cols, "Default Delivery Instruction"),
        delivery_line1: get(cols, "Default Delivery Street Address/PO Box"),
        delivery_line2: get(cols, "Default Delivery Street Address 2"),
        delivery_suburb: get(cols, "Default Delivery Suburb"),
        delivery_city: get(cols, "Default Delivery Town/City"),
        delivery_state: get(cols, "Default Delivery State/Region"),
        delivery_postcode: padAuPostcode(get(cols, "Default Delivery Postal Code")),
        // Physical address (its own columns in the export — previously unmapped).
        physical_line1: get(cols, "Physical Street Address"),
        physical_line2: get(cols, "Physical Street Address 2"),
        physical_suburb: get(cols, "Physical Suburb"),
        physical_suburb_city: get(cols, "Physical Town/City"),
        physical_state: get(cols, "Physical State/Region"),
        physical_postcode: padAuPostcode(get(cols, "Physical Postal Code")),
        first_name: get(cols, "First Name"),
        last_name: get(cols, "Last Name"),
        user_email: get(cols, "Email Address"),
        // Notes + Reminder were previously not written to the shelter; State has no
        // export column (derived from the code). Date Onboarded is set to the import
        // date in runImport (John's call — the import date, not Unleashed's Created On).
        internal_notes: get(cols, "Notes"),
        reminder: get(cols, "Reminder"),
        // ABN — the portal owns it (pushed to Unleashed's GSTVATNumber). Export column is
        // "GST/VAT Number"; a couple of fallbacks in case the header wording differs.
        abn: get(cols, "GST/VAT Number") || get(cols, "GSTVAT Number") || get(cols, "ABN"),
        state: deriveStateFromCode(get(cols, "Customer Code")),
      };
      if (mapped.shelter_code) results.push(mapped);
    }
    const seen = {};
    return results.filter(function (r) {
      if (seen[r.shelter_code]) return false;
      seen[r.shelter_code] = true;
      return true;
    });
  }

  function handleFile(e) {
    setFileErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function (ev) {
      const text = ev.target.result;
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        setFileErr(
          "No valid rows found. Make sure this is the Customer CSV export from Unleashed."
        );
        return;
      }
      setRows(parsed);
      // Check which shelter_codes already exist
      const codes = parsed.map(function (r) {
        return r.shelter_code;
      });
      // Check conflicts against shelters table (org records live there now)
      listSheltersByCodes(codes).then(function (rows) {
        const existing = (rows || []).map(function (r) {
          return r.shelter_code;
        });
        const conflictRows = parsed.filter(function (r) {
          return existing.indexOf(r.shelter_code) !== -1;
        });
        setConflicts(conflictRows);
        const decisions = {};
        conflictRows.forEach(function (r) {
          decisions[r.shelter_code] = "skip";
        });
        setConflictDecisions(decisions);
        setStep("preview");
      });
    };
    reader.readAsText(file);
  }

  async function runImport() {
    setImporting(true);
    setStep("importing");
    const log = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const isConflict = conflicts.some(function (c) {
        return c.shelter_code === row.shelter_code;
      });
      if (isConflict && conflictDecisions[row.shelter_code] === "skip") {
        log.push({ code: row.shelter_code, name: row.shelter_name, status: "skipped" });
        continue;
      }
      // Org fields go to shelters table; caseworker contact fields would go to profiles
      // (CSV import only creates/updates org records — caseworker accounts are created separately)
      const shelterData = {
        shelter_code: row.shelter_code,
        shelter_name: row.shelter_name,
        customer_type: row.customer_type,
        taxable: row.taxable,
        stop_credit: row.stop_credit,
        sales_group: row.sales_group,
        default_warehouse: row.default_warehouse || null, // Unleashed warehouse CODE (db-58)
        postal_line1: row.postal_line1,
        postal_line2: row.postal_line2,
        postal_suburb: row.postal_suburb,
        postal_suburb_city: row.postal_suburb_city,
        postal_state: row.postal_state,
        postal_postcode: row.postal_postcode,
        physical_line1: row.physical_line1,
        physical_line2: row.physical_line2,
        physical_suburb: row.physical_suburb,
        physical_suburb_city: row.physical_suburb_city,
        physical_state: row.physical_state,
        physical_postcode: row.physical_postcode,
        state: row.state || null,
        internal_notes: row.internal_notes || null,
        reminder: row.reminder || null,
        abn: row.abn || null,
        updated_by: user.email,
      };
      try {
        let shelterId = null;
        if (isConflict && conflictDecisions[row.shelter_code] === "update") {
          const upRes = await updateShelterByCode(row.shelter_code, shelterData);
          if (upRes.error) throw new Error(upRes.error.message);
          const upSh = await getShelterByCode(row.shelter_code);
          shelterId = upSh ? upSh.id : null;
          log.push({ code: row.shelter_code, name: row.shelter_name, status: "updated" });
        } else {
          // date_onboarded = the import date ONLY on CREATE (John); an UPDATE never
          // touches it, so real historical onboarded dates are preserved on re-import.
          const insRes = await insertShelter({
            ...shelterData,
            date_onboarded: new Date().toISOString().slice(0, 10),
          });
          if (insRes.error) throw new Error(insRes.error.message);
          shelterId = insRes.id;
          log.push({ code: row.shelter_code, name: row.shelter_name, status: "created" });
        }
        // Default delivery address, if the Customer row carries one. Content-dedupe
        // via planDefaultDeliveryAddress — the old ON CONFLICT (shelter_id,
        // address_name) upsert targeted an index db-129 dropped, and its error was
        // discarded, so this step silently did nothing (finding A2). The read
        // includes deactivated rows on purpose: a re-import must not resurrect an
        // address someone removed. Failures now surface in the per-row import log.
        if (row.delivery_line1 && shelterId) {
          const existingAddrs = await listAllByShelterId(shelterId);
          if (existingAddrs === null) {
            throw new Error("Delivery address: couldn't read the existing addresses");
          }
          const addr = planDefaultDeliveryAddress(
            shelterId,
            {
              street: row.delivery_line1,
              street2: row.delivery_line2,
              suburb: row.delivery_suburb,
              city: row.delivery_city,
              state: row.delivery_state,
              postcode: row.delivery_postcode,
              instruction: row.delivery_instructions,
            },
            existingAddrs
          );
          if (addr) {
            const addrRes = await insertAddresses([addr]);
            if (addrRes.error) throw new Error(`Delivery address: ${addrRes.error.message}`);
          }
        }
        // db-127: bank details → the admin-only shelter_bank_details table. Its
        // ON CONFLICT (shelter_id) target is a real constraint
        // (shelter_bank_details_shelter_id_key), so this upsert works — but the
        // error was discarded, so a failure was invisible. Same reporting gap A2
        // hid behind, without A2's broken conflict target.
        if (shelterId && (row.bank_name || row.bank_account)) {
          const bankRes = await upsertBankDetails(
            shelterId,
            { bank_name: row.bank_name, bank_account: row.bank_account },
            user.email
          );
          if (bankRes.error) throw new Error(`Bank details: ${bankRes.error.message}`);
        }
      } catch (err) {
        log.push({
          code: row.shelter_code,
          name: row.shelter_name,
          status: "error",
          msg: err.message,
        });
      }
    }
    setImportLog(log);
    setImporting(false);
    setStep("done");
  }

  const newRows = rows.filter(function (r) {
    return !conflicts.some(function (c) {
      return c.shelter_code === r.shelter_code;
    });
  });
  const statusColour = {
    created: "#2E7D32",
    updated: "#1565C0",
    skipped: "#607D79",
    error: "#C62828",
  };

  return (
    <div className="p-6 max-w-[900px] mx-auto">
      <h1 className="m-0 mb-1 text-[22px] text-zephyr-text">
        Import Customers/DFV Services from Unleashed
      </h1>
      <p className="m-0 mb-6 text-[13px] text-zephyr-muted">
        Upload a Customer CSV export from Unleashed to import or update DFV Service profiles.
      </p>

      {step === "upload" && (
        <Card style={{ padding: 24 }}>
          <div className="text-[15px] font-bold text-zephyr-teal-dark mb-1">
            Step 1 — Upload Customer CSV
          </div>
          <div className="text-[13px] text-zephyr-muted mb-4">
            In Unleashed: Customers → Export → CSV. Use the full Customer export (not Contacts).
          </div>
          {/* Native file inputs can't be styled — hidden input + a link-styled
              label (bold + underlined), matching Steps 2 & 3 (John). */}
          <label
            htmlFor="customer-import-file"
            className="cursor-pointer font-bold underline text-zephyr-teal-dark text-[13px]"
          >
            📄 Choose the Customer CSV…
          </label>
          <input
            id="customer-import-file"
            type="file"
            accept=".csv,.txt"
            aria-label="Customer CSV file"
            onChange={handleFile}
            className="hidden"
          />
          {fileErr && <div className="text-zephyr-red mt-3 text-[13px]">{fileErr}</div>}
        </Card>
      )}

      {step === "preview" && (
        <div>
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <div className="flex gap-6 text-[13px]">
              <span>
                <strong style={{ color: "#2E7D32" }}>{newRows.length}</strong> new DFV Services to
                create
              </span>
              <span>
                <strong style={{ color: "#F59E0B" }}>{conflicts.length}</strong> existing DFV
                Services (conflicts)
              </span>
              <span>
                <strong>{rows.length}</strong> total rows
              </span>
            </div>
          </Card>

          {conflicts.length > 0 && (
            <Card style={{ padding: 20, marginBottom: 16 }}>
              <div className="font-bold text-[14px] text-zephyr-teal-dark mb-3">
                ⚠️ Conflicts — these DFV Service codes already exist. Choose what to do with each:
              </div>
              {conflicts.map(function (r) {
                return (
                  <div
                    key={r.shelter_code}
                    className="flex items-center gap-[14px] mb-[10px] py-[10px] px-[14px] bg-zephyr-off-white rounded-[8px]"
                  >
                    <div className="flex-1">
                      <span className="font-bold text-zephyr-text">{r.shelter_name}</span>
                      <span className="text-zephyr-muted text-xs ml-2">{r.shelter_code}</span>
                    </div>
                    <select
                      value={conflictDecisions[r.shelter_code] || "skip"}
                      onChange={function (e) {
                        const code = r.shelter_code;
                        const val = e.target.value;
                        setConflictDecisions(function (d) {
                          return Object.assign({}, d, { [code]: val });
                        });
                      }}
                      className="py-[6px] px-[10px] rounded-[6px] border border-zephyr-border text-[13px]"
                      style={{ fontFamily: "inherit" }}
                    >
                      <option value="skip">Skip — keep existing</option>
                      <option value="update">Update — overwrite with CSV data</option>
                    </select>
                  </div>
                );
              })}
            </Card>
          )}

          <Card style={{ padding: 20, marginBottom: 16, maxHeight: 360, overflowY: "auto" }}>
            <div className="font-bold text-[14px] text-zephyr-text mb-3">
              Preview — New DFV Services
            </div>
            {newRows.length === 0 && (
              <div className="text-zephyr-muted text-[13px]">No new DFV Services to create.</div>
            )}
            {newRows.map(function (r) {
              return (
                <div
                  key={r.shelter_code}
                  className="flex gap-3 mb-2 text-[13px] py-2 px-3 bg-zephyr-off-white rounded-[6px]"
                >
                  <span className="font-bold text-zephyr-teal-dark w-[90px]">{r.shelter_code}</span>
                  <span className="flex-1 text-zephyr-text">{r.shelter_name}</span>
                  <span className="text-zephyr-muted">{r.user_email}</span>
                </div>
              );
            })}
          </Card>

          <div className="flex gap-[10px]">
            <Btn
              variant="secondary"
              onClick={function () {
                setStep("upload");
                setRows([]);
                setConflicts([]);
              }}
            >
              Back
            </Btn>
            <Btn onClick={runImport}>
              Run Import (
              {rows.length -
                conflicts.filter(function (c) {
                  return conflictDecisions[c.shelter_code] === "skip";
                }).length}{" "}
              records)
            </Btn>
          </div>
        </div>
      )}

      {step === "importing" && (
        <Card style={{ padding: 40, textAlign: "center" }}>
          <div className="text-[15px] text-zephyr-teal-dark font-bold">Importing...</div>
          <div className="text-[13px] text-zephyr-muted mt-2">
            Please wait, do not close this page.
          </div>
        </Card>
      )}

      {step === "done" && (
        <div>
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <div className="font-bold text-[15px] text-zephyr-teal-dark mb-3">Import Complete</div>
            <div className="flex gap-6 text-[13px] mb-4">
              <span>
                <strong style={{ color: "#2E7D32" }}>
                  {
                    importLog.filter(function (l) {
                      return l.status === "created";
                    }).length
                  }
                </strong>{" "}
                created
              </span>
              <span>
                <strong className="text-zephyr-blue">
                  {
                    importLog.filter(function (l) {
                      return l.status === "updated";
                    }).length
                  }
                </strong>{" "}
                updated
              </span>
              <span>
                <strong className="text-zephyr-muted">
                  {
                    importLog.filter(function (l) {
                      return l.status === "skipped";
                    }).length
                  }
                </strong>{" "}
                skipped
              </span>
              <span>
                <strong className="text-zephyr-red">
                  {
                    importLog.filter(function (l) {
                      return l.status === "error";
                    }).length
                  }
                </strong>{" "}
                errors
              </span>
            </div>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              {importLog.map(function (l, i) {
                return (
                  <div
                    key={i}
                    className={[
                      "flex gap-3 py-[6px] px-[10px] text-[13px] rounded",
                      i % 2 === 0 ? "bg-white" : "bg-zephyr-off-white",
                    ].join(" ")}
                  >
                    <span className="w-[90px] font-bold text-zephyr-teal-dark">{l.code}</span>
                    <span className="flex-1">{l.name}</span>
                    <span className="font-bold" style={{ color: statusColour[l.status] }}>
                      {l.status}
                    </span>
                    {l.msg && <span className="text-zephyr-red text-xs">{l.msg}</span>}
                  </div>
                );
              })}
            </div>
          </Card>
          <Btn
            onClick={function () {
              setStep("upload");
              setRows([]);
              setConflicts([]);
              setImportLog([]);
            }}
          >
            Import Another File
          </Btn>
        </div>
      )}

      {/* A full CSV import of Customers is FIVE sequential steps (John, 4 Aug 2026):
          1 upload Customers (above) · 2 delivery addresses · 3 users/contacts ·
          4 link Customer GUIDs · 5 link Contact GUIDs. The 2-way sync + the raw
          Customer inspector are ongoing utilities, so they sit BELOW the steps. */}

      {/* Step 2 — Import Delivery Addresses (self-contained wizard). */}
      <DeliveryAddressImport />

      {/* Step 3 — Import Users (Customer Contacts): SILENT bulk caseworker-account
          creation (invites go out via the Phase-4c go-live utility). */}
      <ContactsImport />

      {/* Step 4 — Link Customer GUIDs (records shelters.unleashed_guid; no push). */}
      <CustomerLinkSection user={user} />

      {/* Step 5 — Link Contact GUIDs (records user_shelters.unleashed_contact_guid). */}
      <ContactLinkSection user={user} />

      {/* Ongoing utilities — NOT part of the one-time import: the 2-way Customer sync
          (push/pull) and the raw-JSON Customer inspector. */}
      <div style={{ marginTop: 32, borderTop: "1px solid #E0EDEB", paddingTop: 20 }}>
        <h2 className="text-[18px] font-bold text-zephyr-teal-dark mb-1">Utilities</h2>
        <p className="m-0 mb-2 text-[13px] text-zephyr-muted">
          Ongoing tools — not part of the one-time CSV import.
        </p>
        <CustomerSyncSection user={user} />
        <UnleashedInspector />
      </div>
    </div>
  );
}
